import { useState } from "react";
import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { SpanDetailTabs, TabDefinition } from "./SpanDetailTabs";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";
import { safeString } from "../../utils/strings";

export interface LogEventData {
  name?: string;
  data?: {
    message?: string;
    location?: string;
  };
  timestamp?: string;
  background?: boolean;
  background_task_name?: string | null;
}

const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  } catch {
    return timestamp;
  }
};

// Content component for the Log tab
const LogContent = ({ data }: { data: LogEventData }) => {
  return (
    <View className="space-y-4">
      {/* Message */}
      {data.data?.message && (
        <View>
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="message"
              size={14}
              className="text-gray-600 dark:text-gray-300"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Message</Text>
          </View>
          <View className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            <Text className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{safeString(data.data.message)}</Text>
          </View>
        </View>
      )}

      {/* Metadata */}
      <View className="flex flex-row flex-wrap gap-4">
        {/* Location */}
        {data.data?.location && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="code"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-mono">{safeString(data.data.location)}</span>
            </Text>
          </View>
        )}

        {/* Timestamp */}
        {data.timestamp && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="clock"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">{formatTimestamp(data.timestamp)}</Text>
          </View>
        )}

        {/* Background */}
        {data.background && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="layers"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">Background{data.background_task_name ? `: ${data.background_task_name}` : ""}</Text>
          </View>
        )}
      </View>
    </View>
  );
};

// Main LogEventSpanDetailView component with tabs
interface LogEventSpanDetailViewProps {
  data: LogEventData;
  rawData: RawSpanData;
  className?: string;
}

const tabs: TabDefinition[] = [
  { id: "log", label: "Log", color: "gray" },
  { id: "attributes", label: "Attributes", color: "gray" },
];

export const LogEventSpanDetailView = ({ data, rawData, className }: LogEventSpanDetailViewProps) => {
  const [activeTab, setActiveTab] = useState("log");

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      <SpanDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <View className="flex-1 overflow-y-auto p-4">{activeTab === "log" ? <LogContent data={data} /> : <RawSpanDetailView data={rawData} />}</View>
    </View>
  );
};

// Helper to parse log event data from span attributes
export const parseLogEventData = (attributes: Record<string, unknown>): LogEventData | null => {
  const outputValue = attributes["output.value"];
  if (typeof outputValue !== "string") return null;

  try {
    const parsed = JSON.parse(outputValue);
    return parsed as LogEventData;
  } catch {
    return null;
  }
};
